import type { AxiosInstance } from "axios";

import { parseAxiosResponse } from "@thalamos/common";
import type {
  ExtendedVisionUser,
  OnboardUserRequestUnbranded,
  SearchExtendedUsersResponse,
  SearchUsersResponse,
  SetSupervisorSyncExemptionRequestUnbranded,
  UpdateUserProfileRequestUnbranded,
  UserId,
  UserOrganisationMembershipUnbranded,
  UserUpdateRequestUnbranded,
} from "@thalamos/vision-common";
import {
  ExtendedVisionUserSchema,
  SearchExtendedUsersResponseSchema,
  SearchUsersResponseSchema,
} from "@thalamos/vision-common";

import type { ErrorBody, NoContentBody, ResponseBody } from "../axios/api.js";
import { useApiAxios } from "../axios/api.js";

const parseResponseExtendedVisionUser = parseAxiosResponse(
  ExtendedVisionUserSchema,
);
const parseResponseSearchUsers = parseAxiosResponse(SearchUsersResponseSchema);
const parseResponseSearchExtendedUsers = parseAxiosResponse(
  SearchExtendedUsersResponseSchema,
);

export const config = {
  user: {
    me: () => ({
      url: `/api/user/me`,
      method: "get",
      transformResponse: parseResponseExtendedVisionUser,
    }),
    update: (id: UserId, updateUserRequest: UserUpdateRequestUnbranded) => ({
      url: `/api/user/${id}/update`,
      method: "post",
      data: updateUserRequest,
    }),
    onboard: (id: UserId, onboardRequest: OnboardUserRequestUnbranded) => ({
      url: `/api/user/${id}/onboard`,
      method: "post",
      data: onboardRequest,
    }),
    search: (
      query: string,
      limit: number,
      offset: number,
      commandUnitId?: string,
    ) => ({
      url: `/api/user/search`,
      method: "get",
      params: { query, limit, offset, commandUnitId },
      transformResponse: parseResponseSearchUsers,
    }),
    loggedInCheck: () => ({ url: "/api/user/logged-in-check", method: "get" }),
    setSessionContext: (membership: UserOrganisationMembershipUnbranded) => ({
      url: "/api/user/context",
      method: "post",
      data: membership,
    }),
    extendedSearch: (query: string, limit: number, offset: number) => ({
      url: `/api/user/extended-search`,
      method: "get",
      params: { query, limit, offset },
      transformResponse: parseResponseSearchExtendedUsers,
    }),
    setMemberships: (
      userId: UserId,
      memberships: UserOrganisationMembershipUnbranded[],
    ) => ({
      url: `/api/user/${userId}/memberships`,
      method: "post",
      data: memberships,
    }),
    bulkCreate: (csv: string, organisationId: string, dryRun: boolean) => ({
      url: `/api/user/bulk-create?dryRun=${String(dryRun)}`,
      method: "post",
      data: {
        csv,
        organisationId,
      },
    }),
    deleteUser: (userId: UserId) => ({
      url: `/api/user/${userId}/delete`,
      method: "put",
    }),
    updateProfile: (
      userId: UserId,
      data: UpdateUserProfileRequestUnbranded,
    ) => ({
      url: `/api/user/${userId}/update-profile`,
      method: "post",
      data,
    }),
    setSupervisorSyncExemption: (
      userId: UserId,
      data: SetSupervisorSyncExemptionRequestUnbranded,
    ) => ({
      url: `/api/user/${userId}/supervisor-sync-exemption`,
      method: "put",
      data,
    }),
    getSupervisorSyncExemptions: () => ({
      url: `/api/user/supervisor-sync-exemptions`,
      method: "get",
    }),
  },
};

export const api = (apiAxios: AxiosInstance) => {
  return {
    user: {
      me: () => apiAxios.request<ExtendedVisionUser>(config.user.me()),
      update: (id: UserId, updateUserRequest: UserUpdateRequestUnbranded) =>
        apiAxios.request<ResponseBody<NoContentBody, ErrorBody>>(
          config.user.update(id, updateUserRequest),
        ),
      onboard: (id: UserId, onboardRequest: OnboardUserRequestUnbranded) =>
        apiAxios.request<ResponseBody<NoContentBody, ErrorBody>>(
          config.user.onboard(id, onboardRequest),
        ),
      search: (
        query: string,
        limit: number,
        offset: number,
        commandUnitId?: string,
      ) =>
        apiAxios.request<SearchUsersResponse>(
          config.user.search(query, limit, offset, commandUnitId),
        ),
      loggedInCheck: () => apiAxios.request(config.user.loggedInCheck()),

      setSessionContext: (membership: UserOrganisationMembershipUnbranded) =>
        apiAxios.request(config.user.setSessionContext(membership)),
      extendedSearch: (query: string, limit: number, offset: number) =>
        apiAxios.request<SearchExtendedUsersResponse>(
          config.user.extendedSearch(query, limit, offset),
        ),

      setMemberships: (
        userId: UserId,
        memberships: UserOrganisationMembershipUnbranded[],
      ) => {
        return apiAxios.request(
          config.user.setMemberships(userId, memberships),
        );
      },
      bulkCreate: (
        csvData: string,
        organisationId: string,
        dryRun: boolean,
      ) => {
        return apiAxios.request(
          config.user.bulkCreate(csvData, organisationId, dryRun),
        );
      },
      deleteUser: (userId: UserId) => {
        return apiAxios.request(config.user.deleteUser(userId));
      },
      updateUserProfile: (
        userId: UserId,
        data: UpdateUserProfileRequestUnbranded,
      ) => {
        return apiAxios.request(config.user.updateProfile(userId, data));
      },
      setSupervisorSyncExemption: (
        userId: UserId,
        data: SetSupervisorSyncExemptionRequestUnbranded,
      ) => {
        return apiAxios.request(
          config.user.setSupervisorSyncExemption(userId, data),
        );
      },
      getSupervisorSyncExemptions: () => {
        return apiAxios.request<ExtendedVisionUser[]>(
          config.user.getSupervisorSyncExemptions(),
        );
      },
    },
  };
};

export const apiHooks = () => {
  return {
    user: {
      me: function useUserMe() {
        return useApiAxios<ExtendedVisionUser>(config.user.me());
      },
      extendedSearch: function useSearchExtendedUsers(
        query: string,
        limit: number,
        offset: number,
      ) {
        return useApiAxios<SearchExtendedUsersResponse>(
          config.user.extendedSearch(query, limit, offset),
        );
      },
      getSupervisorSyncExemptions: function useGetSupervisorSyncExemptions() {
        return useApiAxios<ExtendedVisionUser[]>(
          config.user.getSupervisorSyncExemptions(),
        );
      },
    },
  };
};
